import { companyPath, contactPath } from 'helper/recordPaths';
import ListIdentity from 'components/record/ListIdentity';
import CardIconButton from 'components/card/CardIconButton';
import {
  ArrowRight,
  Building2Icon,
  ChevronDown,
  CalendarDaysIcon,
  ClipboardListIcon,
  Mail,
  NetworkIcon,
  Pencil,
  Plus,
} from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import MenuButton from 'components/DropdownMenu/MenuButton';
import { SUPER_ADMIN_ROLE } from 'helper/roles';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import RecordActivity from 'components/record/activity/RecordActivity';
import ConfirmDialog from 'components/record/ConfirmDialog';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import { callAction, useDial } from 'components/record/callAction';
import { sourcesActivity } from 'components/record/activity/activityItems';
import ContactRow, { AmbassadorBadge } from 'components/record/ContactRow';
import CompanyPickMenu from 'components/record/CompanyPickMenu';
import { isMissionCompany } from 'components/record/missionCompany';
import ExchangeList from 'components/record/ExchangeList';
import Pagination from 'components/pagination/pagination';
import GroupBillingHistoryCard from 'components/record/GroupBillingHistoryCard';
import KpiTiles from 'components/record/KpiTiles';
import QuickActions from 'components/record/QuickActions';
import RecordFields from 'components/record/RecordFields';
import RecordIdentity, { RecordIcon } from 'components/record/RecordIdentity';
import RecordLayout, { RecordLayoutSkeleton, RecordSection, RecordTabs } from 'components/record/RecordLayout';
import RecordMissions from 'components/record/RecordMissions';
import RecordSourceSelect from 'components/record/RecordSourceSelect';
import useOpenPanel from 'components/record/useOpenPanel';
import { INITIAL_CREATE_MISSION_DATA } from 'components/SlideOver/slideOverUnsavedChanges';
import useSlideOver from 'components/SlideOver/useSlideOver';
import { canCreateMission } from 'components/sidebar/sidebarLinks';
import UseContext from 'hooks/useContext';
import { isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useLatestCall from 'hooks/useLatestCall';
import { useOnChange } from 'hooks/useChangeSignal';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { X as XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import crmApi from 'server/crm/crmApi';
import { companyRoleLine } from 'constants/companyRoles';
import { dateLabel, departmentLabel } from './companiesItems';
import { STAGE_BADGES, exchangeWhen, exchangesOf, fullName, kpiTiles } from './recordItems';
import { toast } from 'react-toastify';

const TABS = ['overview', 'activity', 'missions', 'billing', 'contacts'];
// Past this many, the companies of the overview go page by page.
const COMPANIES_PER_PAGE = 5;

/**
 * The page of a group, on the model of a company's: its own contacts (the HR
 * of a group works for none of its companies), its activity, and its
 * companies, whose numbers it adds up, whose activity it gathers and whose
 * invoices it lists (« Facturation », with the invoice access only). The
 * group, its companies (their stage, their missions, their last exchange),
 * its contacts and its activity (its calls, appointments, tasks and notes,
 * each under its company or the group, and what its clients did in Jobbiz)
 * come from the api, its own phone and e-mail too. A call opens a note on it. Its missions,
 * invoices and contacts each have a menu to read one company (or, for the
 * contacts, the group alone), kept per tab while the page is open.
 */
const GroupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Who opened the page may ask to come back to it (the list on its tab, a
  // task); an address typed or shared comes back to the list.
  const { state: locationState } = useLocation();
  const backTo = typeof locationState?.backTo === 'string' ? locationState.backTo : '/company';
  const { text, language } = useText('record');
  const dial = useDial();
  const { text: activityText } = useText('recordActivity');
  const backLabel = backTo.startsWith('/tasks') ? language.getValue('tasks.titleMine') : text('backCompanies');
  const { call, ready } = useCrmContext();
  // Only the last read lands: a newer one cancels it, which is no error.
  const latest = useLatestCall();
  const { userData } = Usefetchdata();
  const { setDataCreateMission } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const openPanel = useOpenPanel();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const canSeeInvoices = Boolean(userData?.accessInvoice);
  const tabs = TABS.filter((one) => one !== 'billing' || canSeeInvoices);
  const [record, setRecord] = useState(null);
  // undefined while they are added up, null when there are none to show.
  const [invoices, setInvoices] = useState(undefined);
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState('overview');
  const [companyPage, setCompanyPage] = useState(1);
  // The company whose removal from the group waits for its confirmation.
  const [removing, setRemoving] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [deleteGroupBusy, setDeleteGroupBusy] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  // The member taken off one of its companies, waiting for its confirmation.
  const [removingMember, setRemovingMember] = useState(null);
  const [removeMemberBusy, setRemoveMemberBusy] = useState(false);
  // What each tab reads, kept for the visit: 'all', a company, or the group.
  const [missionSource, setMissionSource] = useState('all');
  const [billingSource, setBillingSource] = useState('all');
  const [contactSource, setContactSource] = useState('all');
  // Bumped when a company leaves the group, so that its invoices leave the totals.
  const [invoicesVersion, setInvoicesVersion] = useState(0);

  const load = useCallback(
    () =>
      latest(call(crmApi.getGroupRecord, { groupId: id })).then(
        (next) => {
          setRecord(next);
          setFailed(null);
        },
        (error) => setFailed(errorCodeOf(error) ?? 'UNKNOWN'),
      ),
    [call, latest, id],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  // The invoices of all its companies, added up once the page is shown. The
  // tiles are for every CRM role (decision 34); only the Facturation tab needs
  // the invoices access.
  useEffect(() => {
    if (!ready) return;
    setInvoices(undefined);
    call(crmApi.getGroupInvoiceTotals, { groupId: id }).then((totals) => setInvoices(totals ?? null), () => setInvoices(null));
  }, [call, id, ready, invoicesVersion]);
  useRecordChanged(load);
  // A member taken off a company from its own panel.
  useOnChange('employee_deleted', load);
  useTasksChanged(load);

  const team = useMemo(() => record?.team ?? [], [record]);
  // `fallback`: what names someone the team does not know (« - » for a Wildix call).
  const nameOf = useCallback(
    (personId, fallback = text('someone')) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : fallback;
    },
    [team, text],
  );
  // A source per record: the group alone, then each of its companies.
  const sources = useMemo(
    () =>
      record
        ? [
            { id: record.group.id, label: record.group.name, option: text('groupAlone'), record: record.history },
            ...record.companies.map((company) => ({ id: company.id, label: company.name, companyId: company.id, record: company.history })),
          ]
        : [],
    [record, text],
  );
  const items = useMemo(
    () => sourcesActivity(sources, { now: new Date(), text: activityText, nameOf }),
    [sources, activityText, nameOf],
  );

  if (failed) {
    return (
      <RecordLayout side={null}>
        <CompactEmptyState
          glyph="searchX"
          title={text(failed === 'GROUP_NOT_FOUND' ? 'groupMissing' : 'groupLoadFailed')}
          description={failed === 'GROUP_NOT_FOUND' ? undefined : text('groupLoadFailedText')}
          action={{ label: backLabel, onClick: () => navigate(backTo) }}
        />
      </RecordLayout>
    );
  }
  if (!record) return <RecordLayoutSkeleton />;

  const { group, companies, contacts } = record;
  const canEditContacts = Boolean(record.access?.canEditContacts);
  const companyPages = Math.ceil(companies.length / COMPANIES_PER_PAGE);
  // A group that lost companies keeps a page that still exists.
  const currentCompanyPage = Math.min(companyPage, Math.max(1, companyPages));
  const shownCompanies = companies.slice((currentCompanyPage - 1) * COMPANIES_PER_PAGE, currentCompanyPage * COMPANIES_PER_PAGE);
  // « Créer une mission »: the company is picked among the group's, then the
  // mission form opens on it (its address is filled in by the form's list).
  const createMission = (company) => {
    setDataCreateMission({ ...INITIAL_CREATE_MISSION_DATA, company: company.id, companyName: company.name });
    toggleSlideOver('add_mission');
  };
  const now = new Date();
  const clients = companies.filter((company) => company.stage === 'client').length;
  const clientCompanies = companies.filter((company) => isMissionCompany(company.stage));
  // A contact with an account is sent as that account (userId), else as the group's sub-document (contactId).
  const people = contacts.map((contact) => ({
    id: contact.id,
    ...(contact.userId ? { userId: contact.userId } : { contactId: contact.id }),
    name: fullName(contact),
    email: contact.email,
    phone: contact.phone,
  }));
  const openForm = (kind, extra = {}) => openRecordForm(kind, { groupId: group.id, recordName: group.name, contacts: people, ...extra });
  // A rendez-vous of the group is made in the task form, on « Rendez-vous »,
  // so that it goes to the api linked to the group.
  const openMeetingForm = () => openTaskForm({ defaults: { type: 'meeting', groupId: group.id } });
  const add = (kind) => {
    if (kind === 'task') openTaskForm({ defaults: { type: 'todo', groupId: group.id, scoped: true } });
    else if (kind === 'event') openMeetingForm();
    else openForm(kind === 'all' ? 'note' : kind);
  };
  const openItem = (ref) => {
    // A client's action on a mission opens that mission.
    if (ref.kind === 'client' && ref.missionId) return openPanel('get_mission', ref.missionId);
    if (ref.kind === 'task') openTask(ref.id);
    // A note on a call, linked to the call's company (or the group).
    if (ref.kind === 'call') openForm('note', { callId: ref.id, links: [ref.link] });
    if (ref.kind === 'phoneCall') openForm('note', { phoneCallId: ref.id, links: [ref.link] });
    if (ref.kind === 'client' && ref.companyId) navigate(`${companyPath(ref.companyId)}?onglet=${ref.tab}`);
  };
  // Its companies stay, out of any group, its history is kept; the page goes back to the companies.
  const deleteGroup = () => {
    setDeleteGroupBusy(true);
    call(crmApi.deleteGroup, { groupId: group.id }).then(
      () => {
        toast.success(text('deleteGroupSaved', { name: group.name }));
        navigate('/company', { replace: true });
      },
      (error) => {
        toast.error(apiErrorText(error, { language, text, group: 'deleteGroupErrors' }));
        setDeleteGroupBusy(false);
        setDeletingGroup(false);
      },
    );
  };
  const removeCompany = () => {
    const company = removing;
    setRemoveBusy(true);
    call(crmApi.removeCompanyFromGroup, { groupId: group.id, companyId: company.id })
      .then(
        () => {
          toast.success(text('removeCompanySaved', { company: company.name }));
          load();
          setInvoicesVersion((version) => version + 1);
        },
        (error) => toast.error(apiErrorText(error, { language, text, group: 'removeErrors' })),
      )
      .finally(() => {
        setRemoveBusy(false);
        setRemoving(null);
      });
  };
  // An account is deactivated at the company, never deleted: it can come back
  // from its contact page, its role and perimeters kept.
  const deactivateMember = () => {
    const { company, person } = removingMember;
    setRemoveMemberBusy(true);
    call(crmApi.deactivateCompanyMember, { companyId: company.id, userId: person.id })
      .then(
        () => {
          toast.success(text('contactDeactivatedSaved', { company: company.name }));
          load();
        },
        (error) => toast.error(apiErrorText(error, { language, text, group: 'memberErrors' })),
      )
      .finally(() => {
        setRemoveMemberBusy(false);
        setRemovingMember(null);
      });
  };
  const { upcoming, recent } = exchangesOf(items, 4);
  const exchange = (item) => ({
    key: item.key,
    title: item.title,
    detail: [item.from, item.body].filter(Boolean).join(' · '),
    when: exchangeWhen(item),
    onOpen: item.ref ? () => openItem(item.ref) : undefined,
  });
  const companyIds = companies.map((company) => company.id);
  // A company picked, then removed from the group: back to all of them.
  const companyPicked = (value) => (companyIds.includes(value) ? value : 'all');
  const missionCompany = companyPicked(missionSource);
  const billingCompany = companyPicked(billingSource);
  const contactPicked = contactSource === group.id ? group.id : companyPicked(contactSource);
  const companySources = companies.map((company) => ({ id: company.id, label: company.name }));
  const missionCompanies = companies.filter((company) => missionCompany === 'all' || company.id === missionCompany);
  const contactCompanies = companies.filter((company) => contactPicked === 'all' || company.id === contactPicked);
  const showGroupContacts = contactPicked === 'all' || contactPicked === group.id;
  const contactDetail = (person) => [person.position, person.phone, person.email].filter(Boolean).join(' · ');

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: backLabel, onClick: () => navigate(backTo) }}
            // As on a company: its actions in a menu; deleting it is the agency manager's.
            menu={
              userData?.role === SUPER_ADMIN_ROLE && (
                <MenuButton
                  trigger={(toggleMenu) => (
                    <Button color="SECONDARY" size="SM" onClick={toggleMenu}>
                      <span>{text('actions')}</span>
                      <ChevronDown wh={18} color="currentColor" />
                    </Button>
                  )}
                  items={[{ label: text('deleteGroup'), onClick: () => setDeletingGroup(true), className: 'text-red-600' }]}
                />
              )
            }
            picture={<RecordIcon icon={NetworkIcon} />}
            title={<span className="uppercase">{group.name}</span>}
            badge={<Badge type="INFO">{text('group')}</Badge>}
            subtitle={[
              text(companies.length > 1 ? 'companiesMany' : 'companiesOne', { count: companies.length }),
              clients > 0 && text(clients > 1 ? 'clientsMany' : 'clientsOne', { count: clients }),
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                callAction({ label: text('actionCall'), people: contacts, onNone: () => toast.info(text('noPhone')), dial }),
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: openMeetingForm },
              ].filter(Boolean)}
            />
          </RecordIdentity>
          {/* As a company's « À propos », the group's own way to reach it. */}
          <RecordSection
            title={text('groupInfoTitle')}
            action={
              <CardIconButton
                label={text('groupInfoEdit')}
                icon={<Pencil wh={18} color="#374151" />}
                onClick={() => openForm('groupInfo', { info: { phone: group.phone, email: group.email } })}
              />
            }
          >
            <RecordFields
              fields={[
                { label: text('groupInfoPhone'), value: group.phone },
                { label: text('groupInfoEmail'), value: group.email },
              ]}
            />
          </RecordSection>
          <RecordSection
            title={text('groupContactsTitle', { count: contacts.length })}
            action={
              canEditContacts && (
                <Button color="SECONDARY" size="SM" onClick={() => openForm('groupContact')}>
                  {text('add')}
                </Button>
              )
            }
          >
            {!contacts.length && <p className="text-sm text-gray-600">{text('groupContactsEmpty')}</p>}
            {contacts.slice(0, 3).map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => navigate(contactPath(person.id))}
                className="-mx-2 block w-[calc(100%+16px)] rounded-lg border-b border-gray-100 px-2 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm text-gray-900">{fullName(person)}</span>
                  {person.leadStatus === 'ambassadeur' && <AmbassadorBadge />}
                </span>
                <span className="block truncate text-xs text-gray-700">{person.position || text('noPosition')}</span>
              </button>
            ))}
            {contacts.length > 0 && (
              <Button color="SECONDARY" size="SM" className="mt-3 w-full" onClick={() => setTab('contacts')}>
                {text('allContacts')}
              </Button>
            )}
          </RecordSection>
        </>
      }
    >
      <KpiTiles
        tiles={kpiTiles(
          {
            ...record.kpis,
            invoices: invoices ?? null,
            invoicesLoading: invoices === undefined,
            stage: clients ? 'client' : 'prospect',
          },
          text,
        )}
        actions={{
          missions: () => setTab('missions'),
          ...(tabs.includes('billing') && { revenue: () => setTab('billing'), unpaid: () => setTab('billing') }),
        }}
      />
      <RecordTabs tabs={tabs.map((one) => ({ id: one, label: text(`tab_${one}`) }))} activeTab={tab} onChange={setTab} />
      {tab === 'overview' && (
        <div className="flex flex-col gap-4 pb-6">
          <RecordSection
            title={text('groupCompanies', { count: companies.length })}
            action={
              <Button color="SECONDARY" size="SM" onClick={() => openForm('groupCompany')}>
                <Plus wh={18} color="currentColor" />
                <span>{text('addCompany')}</span>
              </Button>
            }
          >
            {!companies.length && (
              <CompactEmptyState compact glyph="searchX" title={text('groupCompaniesEmptyTitle')} description={text('groupCompaniesEmptyText')} />
            )}
            {shownCompanies.map((company) => (
              <div key={company.id} className="-mx-2 flex w-[calc(100%+16px)] items-center gap-1 border-b border-gray-100 last:border-0">
                <button
                  type="button"
                  onClick={() => navigate(companyPath(company.id))}
                  className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_88px_110px_130px] items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <ListIdentity
                    icon={Building2Icon}
                    name={company.name}
                    uppercase
                    detail={departmentLabel(company.department) || text('noDepartment')}
                  />
                  <span>
                    <Badge type={STAGE_BADGES[company.stage]}>{text(`stage_${company.stage}`)}</Badge>
                  </span>
                  <span className="text-right text-sm text-gray-900">
                    {text(company.missionCount > 1 ? 'missionsMany' : 'missionsOne', { count: company.missionCount })}
                  </span>
                  <span className="text-right text-xs text-gray-600">
                    {company.lastExchangeAt
                      ? text('lastExchange', { day: dateLabel(isoDay(new Date(company.lastExchangeAt)), now) })
                      : text('noExchange')}
                  </span>
                </button>
                <CardIconButton
                  label={text('removeCompany', { company: company.name })}
                  icon={<XIcon size={18} color="#374151" />}
                  onClick={() => setRemoving(company)}
                />
              </div>
            ))}
            {companyPages > 1 && (
              <div className="mt-3">
                <Pagination
                  currentPage={currentCompanyPage}
                  countPerPage={COMPANIES_PER_PAGE}
                  countTotal={companies.length}
                  totalPages={companyPages}
                  setCurrentPage={setCompanyPage}
                  customText={text('groupCompaniesRange', {
                    from: (currentCompanyPage - 1) * COMPANIES_PER_PAGE + 1,
                    to: Math.min(currentCompanyPage * COMPANIES_PER_PAGE, companies.length),
                    count: companies.length,
                  })}
                />
              </div>
            )}
          </RecordSection>
          <div className="grid grid-cols-1 gap-4 tab:grid-cols-2">
            <ExchangeList
              title={text('upcoming')}
              items={upcoming.map(exchange)}
              empty={{ icon: CalendarDaysIcon, title: text('upcomingEmptyTitle'), description: text('groupUpcomingEmptyText') }}
            />
            <ExchangeList
              title={text('recent')}
              items={recent.map(exchange)}
              empty={{ glyph: 'inbox', title: text('recentEmptyTitle'), description: text('groupRecentEmptyText') }}
            />
          </div>
        </div>
      )}
      {tab === 'activity' && (
        <RecordActivity
          items={items}
          sources={sources.map((source) => ({ id: source.id, label: source.option ?? source.label }))}
          allLabel={text('groupAndCompanies')}
          onAdd={add}
          onOpen={openItem}
        />
      )}
      {tab === 'missions' && (
        <RecordMissions
          missions={missionCompanies.flatMap((company) => company.missions.missions)}
          tempWorkers={missionCompanies.flatMap((company) => company.missions.tempWorkers)}
          action={
            companies.length > 0 && (
              <div className="flex min-w-0 items-center gap-3">
                <RecordSourceSelect
                  value={missionCompany}
                  sources={companySources}
                  allLabel={text('allCompanies')}
                  onChange={setMissionSource}
                />
                {canCreateMission(userData) && clientCompanies.length > 0 && (
                  <CompanyPickMenu
                    label={text('createMission')}
                    companies={clientCompanies.map((company) => ({
                      id: company.id,
                      name: company.name,
                      detail: departmentLabel(company.department) || text('noDepartment'),
                    }))}
                    onPick={createMission}
                  />
                )}
              </div>
            )
          }
        />
      )}
      {tab === 'billing' && canSeeInvoices && (
        <div className="flex flex-col gap-4 pb-6 text-sm">
          {companies.length ? (
            <>
              <div className="flex justify-end">
                <RecordSourceSelect
                  value={billingCompany}
                  sources={companySources}
                  allLabel={text('allCompanies')}
                  onChange={setBillingSource}
                />
              </div>
              <GroupBillingHistoryCard
                companies={companies.map((company) => ({ id: company.id, name: company.name }))}
                companyId={billingCompany === 'all' ? null : billingCompany}
              />
            </>
          ) : (
            <CompactEmptyState compact glyph="searchX" title={text('groupCompaniesEmptyTitle')} description={text('groupBillingNoCompany')} />
          )}
        </div>
      )}
      {tab === 'contacts' && (
        <div className="flex flex-col gap-4 pb-6">
          <div className="flex justify-end">
            <RecordSourceSelect
              value={contactPicked}
              sources={[{ id: group.id, label: text('groupAlone') }, ...companySources]}
              allLabel={text('groupAndCompanies')}
              onChange={setContactSource}
            />
          </div>
          {showGroupContacts && (
            <RecordSection
              title={text('groupContactsTitle', { count: contacts.length })}
              action={
                canEditContacts && (
                  <Button color="SECONDARY" size="SM" onClick={() => openForm('groupContact')}>
                    <Plus wh={18} color="currentColor" />
                    <span>{text('addContact')}</span>
                  </Button>
                )
              }
            >
              {!contacts.length && (
                <CompactEmptyState compact glyph="inbox" title={text('groupContactsEmptyTitle')} description={text('groupContactsEmptyText')} />
              )}
              {contacts.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={contactDetail(person)}
                  onOpen={() => navigate(contactPath(person.id))}
                  actions={
                    canEditContacts && (
                      <CardIconButton
                        label={text('editContact', { name: fullName(person) })}
                        icon={<Pencil wh={18} color="#374151" />}
                        onClick={() => openForm('groupContact', { contact: person })}
                      />
                    )
                  }
                />
              ))}
            </RecordSection>
          )}
          {contactCompanies.map((company) => (
            <RecordSection key={company.id}>
              {/* The company as in « Entreprises du groupe », its contacts counted under its name. */}
              <div className="mb-1 flex items-center justify-between gap-3 py-2.5">
                <ListIdentity
                  icon={Building2Icon}
                  name={company.name}
                  uppercase
                  detail={text(company.members.length > 1 ? 'contactsMany' : 'contactsOne', { count: company.members.length })}
                />
                <CardIconButton
                  label="Voir l’entreprise"
                  ariaLabel={`Voir ${company.name}`}
                  icon={<ArrowRight wh={18} color="#374151" />}
                  onClick={() => navigate(companyPath(company.id))}
                />
              </div>
              {!company.members.length && <p className="text-sm text-gray-600">{text('companyNoContact')}</p>}
              {company.members.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  // Their role at this company: the same person may have another elsewhere.
                  detail={[person.role && companyRoleLine(language, person.role), person.position].filter(Boolean).join(' · ')}
                  onOpen={() => navigate(contactPath(person.id))}
                  hoverActions={
                    <CardIconButton
                      label={text('memberRemove')}
                      ariaLabel={text('contactRemoveFromLabel', { name: fullName(person), company: company.name })}
                      icon={<XIcon size={18} color="#374151" />}
                      onClick={() => setRemovingMember({ company, person })}
                    />
                  }
                />
              ))}
            </RecordSection>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={Boolean(removing)}
        busy={removeBusy}
        title={removing ? text('removeCompanyTitle', { company: removing.name }) : ''}
        cancelLabel={text('removeCompanyCancel')}
        confirmLabel={text('removeCompanyConfirm')}
        onCancel={() => setRemoving(null)}
        onConfirm={removeCompany}
      />
      <ConfirmDialog
        open={Boolean(removingMember)}
        busy={removeMemberBusy}
        title={
          removingMember
            ? text('contactRemoveTitle', { name: fullName(removingMember.person), company: removingMember.company.name })
            : ''
        }
        description={removingMember ? text('contactRemoveAccountText') : ''}
        cancelLabel={text('contactRemoveCancel')}
        confirmLabel={text('contactRemoveConfirm')}
        onCancel={() => setRemovingMember(null)}
        onConfirm={deactivateMember}
      />
      <ConfirmDialog
        open={deletingGroup}
        busy={deleteGroupBusy}
        title={text('deleteGroupTitle', { name: group.name })}
        description={text('deleteGroupText')}
        cancelLabel={text('deleteGroupCancel')}
        confirmLabel={text('deleteGroupConfirm')}
        onCancel={() => setDeletingGroup(false)}
        onConfirm={deleteGroup}
      />
    </RecordLayout>
  );
};

export default GroupPage;
